import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { CustomButton } from '../CustomButton';
import { useDropzone } from 'react-dropzone';
import { ReactComponent as PlusIcon } from '../../assets/images/plus.svg';
import { ReactComponent as AttachFileIcon } from '../../assets/images/attach-file.svg';
import { ReactComponent as Delete } from '../../assets/images/delete.svg';
import { ReactComponent as FileIcon } from '../../assets/images/fileIcon.svg';
import { IMAGE_ACCEPT } from '../../constants/variables';
import { ReactComponent as ReportRedIcon } from '../../assets/images/report_red.svg';
import { CustomModal } from '../CustomModal';

export const UploadFiles = ({
  description,
  setFieldValue,
  name,
  defaultFiles,
  uploadFetch,
  validationUpload,
  isAllowedToRemoveFile = true,
  title,
  iconPlus = false,
  image = false,
  deleteFetch,
  resultModal
}) => {
  const [currentFiles, setCurrentFiles] = useState(defaultFiles);
  const [errorModalText, setErrorModalText] = useState({
    main: '',
    secondary: ''
  });
  const [errorModal, setErrorModal] = useState(false);

  useEffect(() => {
    setCurrentFiles(defaultFiles);
  }, [defaultFiles])

  const onDrop = (acceptedFiles) => {
    if (image) {
      const file = acceptedFiles[0];
      const previousFiles = currentFiles;
      setCurrentFiles([file]);
      if (!uploadFetch) {
        setFieldValue(name, [file]);
        return;
      }
      // uploadFetch owns the field value on success; on failure the field keeps its previous value.
      Promise.resolve(uploadFetch(file)).catch((error) => {
        setCurrentFiles(previousFiles);
        setErrorModalText({
          main: `Файл ${file.name} не загружен`,
          secondary: error?.message
        });
        setErrorModal(true);
      });
    } else {
      if (uploadFetch && currentFiles.length < 50) uploadFetch(acceptedFiles).then(res => {
        if (res.status !== 400 && res.data.instance) {
          acceptedFiles[0].id = res.data.instance.id;
          acceptedFiles[0].newFile = true;
          setCurrentFiles([...currentFiles, ...acceptedFiles]);
          setFieldValue(name, [...currentFiles, ...acceptedFiles]);
          resultModal && resultModal();
        } else {
          setErrorModalText({
            main: `Файл ${acceptedFiles[0].name} не валиден!`,
            secondary: res.data.detail
          })
          setErrorModal(true);
        }
      });
    }
  };

  const CustomValidation = (file) => {
    const result = validationUpload(file, currentFiles);
    if (result) return result;
    return null;
  };

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    accept: image ? IMAGE_ACCEPT : '.xls, .xlsx',
    validator: validationUpload && CustomValidation
  });

  const removeFile = (file) => () => {
    if (deleteFetch) {
      deleteFetch(file?.id).then(res => {
        if (res.status !== 400) {
          const newFiles = [...currentFiles];
          newFiles.splice(newFiles.indexOf(file), 1);
          setCurrentFiles(newFiles);
          setFieldValue(name, newFiles);
        }
      })
    } else {
      const newFiles = [...currentFiles];
      newFiles.splice(newFiles.indexOf(file), 1);
      setCurrentFiles(newFiles);
      setFieldValue(name, newFiles);
    }
  };

  return (
    <Box>
      <Box
        {...getRootProps({
          className: 'dropzone multiple'
        })}>
        <input {...getInputProps()} multiple={false} />
        <Box>
          <CustomButton
            sx={{
              border: '1px solid #DDE1E5',
              boxSizing: ' border-box',
              borderRadius: '12px',
              padding: '10px 16px !important',
              color: '#151C22'
            }}
            size="small">
            <Box mr={1} display="flex" alignItems="center">
              {iconPlus ? <PlusIcon /> : <AttachFileIcon />}{' '}
            </Box>
            {title || 'Добавить файлы'}
          </CustomButton>
        </Box>
      </Box>

      {description && (
        <Box mt={0.5} mb={1}>
          <Typography
            sx={{
              color: '#667A8B',
              fontSize: '1rem'
            }}>
            {description}
          </Typography>
        </Box>
      )}

      {currentFiles && currentFiles.length > 0 && (
        <Box display="flex" flexDirection="column" maxHeight="260px" overflow="auto">
          {(currentFiles).map((file, index) => (
            <Box key={`${file.path || file.file_name}_${index}`} display="flex" mt>
              <Box
                sx={{
                  border: '1px solid #DDE1E5',
                  padding: '7px !important',
                  boxSizing: ' border-box',
                  borderRadius: '12px',
                  color: '#151C22',
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'grey.600',
                  maxWidth: '530px',
                  width: '100%'
                }}>
                <Box display="flex" alignItems="center">
                  <FileIcon height="20px" width="20px" />
                </Box>

                <Typography
                  sx={{
                    color: '#667A8B',
                    fontSize: '1rem',
                    maxWidth: '544px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    ml: '8px'
                  }}>
                  {file.path || file.file_name}
                  <Box as="span"> ({file.size && file.size / 1000}kb)</Box>
                </Typography>

                <Box
                  ml="auto"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: isAllowedToRemoveFile || (!isAllowedToRemoveFile && file.newFile) ? 'pointer' : 'not-allowed',
                  }}
                  onClick={(isAllowedToRemoveFile || (!isAllowedToRemoveFile && file.newFile)) && removeFile(file)}
                  data-testid="DeleteIcon"
                >
                  <Delete disabled={!(isAllowedToRemoveFile || (!isAllowedToRemoveFile && file.newFile))} />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {fileRejections?.map(({ errors }) => (
        <Box m="5px 0px 5px 0px " key={errors.code} color="error.main">
          {errors[0].message}
        </Box>
      ))}
      <CustomModal
        opened={errorModal}
        modalClose={() => setErrorModal(false)}
        text={errorModalText.main}
        subText={errorModalText.secondary}
        cancelBtnName="Готово"
        withoutSubmit
        icon={<ReportRedIcon />}
        sx={{
          '& > .MuiDialog-container': {
            '& > .MuiDialog-paper': {
              maxWidth: '800px'
            }
          }
        }}
      />
    </Box>
  );
};

UploadFiles.propTypes = {
  description: PropTypes.string,
  name: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  setFiles: PropTypes.func,
  maxUploadFiles: PropTypes.number,
  setFieldValue: PropTypes.func,
  uploadFetch: PropTypes.func,
  defaultFiles: PropTypes.array,
  noMoreMb: PropTypes.number,
  validationUpload: PropTypes.func,
  isAllowedToRemoveFile: PropTypes.bool,
  title: PropTypes.string,
  iconPlus: PropTypes.bool,
  image: PropTypes.bool,
  deleteFetch: PropTypes.func,
  resultModal: PropTypes.func
};
